import { TFunction } from 'i18next';

interface ExecutionErrorDetails {
  message: string;
  name: string;
  isTimeout?: boolean;
}

interface SuccessResult {
  status: 'success';
  result: any;
}

interface ErrorResult {
  status: 'error';
  error: string | ExecutionErrorDetails;
}

export type ExecutionResult = SuccessResult | ErrorResult;

/**
 * Checks if the code contains suspicious elements
 * @param {string} code - Code to verify
 * @returns {boolean} True if suspicious code is detected
 */
export function containsSuspiciousCode(code: string, t: TFunction<'translation', undefined>): string | null {
  const suspiciousPatterns: RegExp[] = [
    /require\s*\(/i,
    /import\s+(?:.|[\r\n])*?from/i,
    /process/i,
    /child_process/i,
    /fs\s*\./i,
    /__dirname/i,
    /__filename/i,
    /eval\s*\(/i,
    /Function\s*\(/i,
    /localStorage/i,
    /sessionStorage/i,
    /indexedDB/i,
    /crypto\.subtle/i,
    /navigator\./i,
    /document\./i,
    /window\./i,
    /globalThis\./i,
    /global\./i,
    /constructor\.constructor/i,
  ];

  for (const pattern of suspiciousPatterns) {
    const match = code.match(pattern);
    if (match && match.index !== undefined) {
      const index = match.index;

      // Extract the full word at that position
      const fullWord = code.slice(index).match(/^\w+/)?.[0] || match[0];

      return t('JSCode/executeError', {
        pattern: match[0],
        matchedTerm: fullWord,
      });
    }
  }
  return null;
}

/**
 * Executes user JavaScript code securely in a Web Worker with a timeout
 * @param {string} code - User's JavaScript code
 * @param {Record<string, any>} context - Context variables
 * @param {number} timeout - Timeout in milliseconds
 * @returns {Promise<ExecutionResult>}
 */
export function executeUserCode(
  code: string,
  context: Record<string, any> = {},
  timeout = 250,
  t: TFunction<'translation', undefined>
): Promise<ExecutionResult> {
  const result = containsSuspiciousCode(code, t);
  if (result !== null) {
    return Promise.resolve({
      status: 'error',
      error: result,
    });
  }

  return new Promise<ExecutionResult>(resolve => {
    const workerCode = `
      self.onmessage = function(e) {
        const { code, context } = e.data;
        try {
          const keys = Object.keys(context || {});
          const values = Object.values(context || {});
          const fn = new Function(...keys, \`"use strict";\\n\${code}\`);
          const result = fn(...values);
          self.postMessage({ status: "success", result });
        } catch (err) {
          self.postMessage({ status: "error", error: { message: err.message, name: err.name } });
        }
      };
    `;

    const blob = new Blob([workerCode], { type: 'application/javascript' });
    const worker = new Worker(URL.createObjectURL(blob));
    let resolved = false;

    const timer = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        worker.terminate();
        resolve({
          status: 'error',
          error: {
            message: 'The code execution time exceeded the allowed limit of 250 milliseconds.',
            name: 'TimeoutError',
            isTimeout: true,
          },
        });
      }
    }, timeout);

    worker.onmessage = e => {
      if (!resolved) {
        resolved = true;
        clearTimeout(timer);
        worker.terminate();
        resolve(e.data);
      }
    };

    worker.onerror = e => {
      if (!resolved) {
        resolved = true;
        clearTimeout(timer);
        worker.terminate();
        resolve({
          status: 'error',
          error: {
            message: e.message,
            name: 'WorkerError',
          },
        });
      }
    };

    worker.postMessage({ code, context });
  });
}
